import React from "react";
import { ArrowRight } from "lucide-react";
import Reveal from "./Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { useContent } from "../content/siteContent";
import useDaysUntil from "../hooks/useDaysUntil";

export default function Investors() {
  const { t } = useLanguage();
  const { content } = useContent();
  const inv = t.investors;
  const days = useDaysUntil(content.betaDate);
  const cells = [
    { v: content.traction.clinics, l: inv.statLabels.clinics },
    { v: content.traction.patients, l: inv.statLabels.patients },
    { v: content.traction.transactions, l: inv.statLabels.transactions },
    { v: days ?? "—", l: inv.statLabels.daysToBeta, accent: true },
  ];

  return (
    <section id="investors" data-testid="investors-section" className="py-24 sm:py-32 border-t border-line">
      <div className="shell">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <Reveal className="kicker text-textsoft">{inv.kicker}</Reveal>
            <Reveal as="h2" delay={1} className="mt-5 font-display text-[32px] sm:text-[42px] lg:text-[48px] font-semibold text-ink tracking-tight leading-[1.08]">
              {inv.h2}
            </Reveal>

            <ol className="mt-10 space-y-8">
              {inv.thesis.map((point, i) => (
                <Reveal as="li" delay={Math.min(i + 1, 4)} key={point.l} className="flex gap-5">
                  <span className="font-mono text-[13px] font-semibold text-blue pt-1 w-7 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="font-display text-[18px] font-semibold text-ink tracking-tight">{point.l}</div>
                    <p className="mt-2 text-[15px] text-textsoft leading-[1.6] max-w-[520px]">{point.b}</p>
                  </div>
                </Reveal>
              ))}
            </ol>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={2} className="lg:sticky lg:top-24">
              <div className="glow grad-border rounded-[22px] p-7 sm:p-8 text-white" style={{ background: "linear-gradient(165deg, #163A5C 0%, #0F2A47 65%)", boxShadow: "0 40px 90px -36px rgba(15,42,71,0.5)" }}>
                <div className="flex items-center gap-2">
                  <span className="live-dot" />
                  <span className="kicker text-white/55">{inv.tractionLabel}</span>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-px rounded-2xl overflow-hidden bg-white/[0.08]">
                  {cells.map((s) => (
                    <div key={s.l} className="bg-[#153353] p-5">
                      <div className={`font-display text-[26px] font-semibold tracking-tight ${s.accent ? "text-white" : "text-green"}`}>{s.v}</div>
                      <div className="mt-1.5 text-[11.5px] text-white/55 leading-snug">{s.l}</div>
                    </div>
                  ))}
                </div>

                <a href="#demo" data-testid="investors-cta" className="btn btn-primary w-full mt-6 group">
                  {inv.cta}
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20 group-hover:translate-x-0.5 transition-transform">
                    <ArrowRight size={13} strokeWidth={2.4} />
                  </span>
                </a>

                <p className="mt-4 text-[11.5px] italic text-white/40 leading-[1.5]">{inv.note}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
